// src/utils/techCompletion.js
//
// TECH "MARK AS COMPLETED" -> OFFICE REVIEW
//
// When a Tech marks a job completed, nothing on the job itself changes:
// no status, no completedDates, no dates, no color. The calendar every tech
// sees stays exactly as it was. Instead, one request doc is written to its
// own collection (techCompletionRequests), keyed jobId__occurrenceKey, so:
//   - recurring jobs are occurrence-aware (January's request doesn't cover
//     February's visit);
//   - submitting twice just updates the same request, never duplicates it.
//
// Admin / Manager / Assistant see the open requests in the "Ready to Invoice"
// panel on Home. They open the job and use the SAME Mark Job as Completed
// button they use today, so their flow, privileges and the invoice prompt
// are unchanged. As soon as the job (or that occurrence) is completed by
// staff, the panel marks the request "done" on its own.
//
// Request status: "pending" (waiting on office) | "returned" (sent back to
// the tech with a reason) | "done" (office completed it).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getViewAs, silentlyBlockedByViewAs } from "./viewAs";
import moment from "moment";
import { db } from "../firebaseConfig";
import { useLanguage, TranslatedText, tNow, getCurrentLanguage } from "../i18n";
import {
  getRecurringOccurrenceKey,
  isOccurrenceClosedFor,
  isRecurringJobRecord,
} from "./recurringEditUtils";

export const TECH_COMPLETION_COLLECTION = "techCompletionRequests";

// Firestore rejects IDs containing "/" or matching __.*__
const buildRequestId = (jobId, occurrenceKey) =>
  `${String(jobId).replace(/\//g, "-")}__${String(occurrenceKey || "none").replace(/\//g, "-")}`;

const occurrenceKeyFor = (job) => {
  if (!job) return "";
  if (!isRecurringJobRecord(job)) return "main";
  return getRecurringOccurrenceKey(job) || moment().format("YYYY-MM-DD");
};

export const getTechCompletionRequestId = (job) =>
  job?.id ? buildRequestId(job.id, occurrenceKeyFor(job)) : "";

/* ---------------------------------------------------------------------- */
/* Tech side                                                              */
/* ---------------------------------------------------------------------- */

export const submitTechCompletion = async (job, { techName = "", note = "", lang = "" } = {}) => {
  if (silentlyBlockedByViewAs()) throw new Error(tNow("View as is read-only. Nothing was saved. (You're viewing as {name}.)", { name: getViewAs()?.name || "" }));
  if (!job?.id) throw new Error(tNow("No job selected."));

  const occurrenceKey = occurrenceKeyFor(job);
  const recurring = isRecurringJobRecord(job);
  const user = getAuth().currentUser;

  const payload = {
    jobId: job.id,
    occurrenceKey,
    recurring,
    // the date the tech was looking at, for display
    serviceDate:
      (recurring ? occurrenceKey : job.__renderDate || job.startDate || "") || "",
    wo: job.wo || "",
    siteName: job.siteName || "",
    address: job.address || "",
    city: job.city || "",
    serviceType: job.serviceType || "",
    techAssign: Array.isArray(job.techAssign)
      ? job.techAssign
      : job.techAssign
      ? [job.techAssign]
      : [],
    submittedBy: techName || user?.displayName || user?.email || "Tech",
    submittedByUid: user?.uid || "",
    submittedAt: serverTimestamp(),
    techNote: String(note || "").trim(),
    // language the tech was using when they wrote techNote ("en" / "es")
    lang: lang || getCurrentLanguage() || "en",
    status: "pending",
    // clear any earlier "sent back" info when resubmitting
    returnedBy: "",
    returnReason: "",
  };

  await setDoc(doc(db, TECH_COMPLETION_COLLECTION, buildRequestId(job.id, occurrenceKey)), payload, {
    merge: true,
  });

  return { ...payload, id: buildRequestId(job.id, occurrenceKey), submittedAt: new Date() };
};

// Loads the request (if any) for the job/occurrence currently open.
export const useTechCompletionRequest = (job) => {
  const [request, setRequest] = useState(null);
  const requestId = getTechCompletionRequestId(job);

  const reload = useCallback(async () => {
    if (!requestId) {
      setRequest(null);
      return;
    }
    try {
      const snap = await getDoc(doc(db, TECH_COMPLETION_COLLECTION, requestId));
      setRequest(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    } catch (err) {
      console.error("Could not load tech completion request:", err);
      setRequest(null);
    }
  }, [requestId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { request, setRequest, reload };
};

const formatWhen = (value) => {
  if (!value) return "";
  const d = value?.toDate ? value.toDate() : value instanceof Date ? value : new Date(value);
  const m = moment(d);
  return m.isValid() ? m.format("MM/DD/YYYY h:mm A") : "";
};

// Small banner shown inside the tech's job popup.
export const TechCompletionStatus = ({ request }) => {
  const { t } = useLanguage();
  if (!request) return null;

  if (request.status === "pending") {
    return (
      <div style={bannerStyle("#ecfdf5", "#10b981", "#065f46")}>
        ✅{" "}
        {request.submittedAt
          ? t("Sent to the office as completed on {when}. Waiting for the office to finish it.", {
              when: formatWhen(request.submittedAt),
            })
          : t("Sent to the office as completed. Waiting for the office to finish it.")}
      </div>
    );
  }

  if (request.status === "returned") {
    return (
      <div style={bannerStyle("#fff7ed", "#f97316", "#7c2d12")}>
        ↩ {t("Sent back by {name}", { name: request.returnedBy || t("the office") })}
        {request.returnReason ? (
          <>
            : "<TranslatedText text={request.returnReason} showToggle={false} />"
          </>
        ) : (
          ""
        )}
        . {t("Fix it and mark it completed again.")}
      </div>
    );
  }

  if (request.status === "done") {
    return (
      <div style={bannerStyle("#eff6ff", "#3b82f6", "#1e3a8a")}>
        ✔ {t("The office completed this job.")}
      </div>
    );
  }

  return null;
};

const bannerStyle = (bg, border, color) => ({
  margin: "8px 0 12px",
  padding: "10px 12px",
  borderRadius: 10,
  background: bg,
  border: `1px solid ${border}`,
  color,
  fontWeight: 700,
  fontSize: 14,
});

// The tech's confirm dialog (replaces the staff "Complete this job?" popup
// for Tech only).
export const TechCompleteDialog = ({ job, techName, onClose, onSubmitted }) => {
  const { t, lang } = useLanguage();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setSaving(true);
    try {
      const saved = await submitTechCompletion(job, { techName, note, lang });
      if (typeof onSubmitted === "function") await onSubmitted(saved);
    } catch (err) {
      console.error("Could not send completion to office:", err);
      alert(err?.message || t("Could not send this to the office. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={() => !saving && onClose()} />
      <div className="modal">
        <h3 style={{ marginTop: 0 }}>{t("Mark job as completed?")}</h3>
        <p style={{ marginTop: 0 }}>
          {t("This sends the job to the office so they can finish it and make the invoice. Your calendar won't change.")}
        </p>
        <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
          {t("Note for the office (optional)")}
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("Anything they should know before invoicing")}
          />
        </label>
        <div className="modal-buttons">
          <button className="green-button" onClick={send} disabled={saving}>
            {saving ? t("Sending...") : t("Yes, send to office")}
          </button>
          <button className="red-button" onClick={onClose} disabled={saving}>
            {t("No")}
          </button>
        </div>
      </div>
    </>
  );
};

/* ---------------------------------------------------------------------- */
/* Office side - "Ready to Invoice" panel                                  */
/* ---------------------------------------------------------------------- */

// Turns a request + master job into the occurrence object every page's
// completion code expects (same shape the calendars render).
export const buildJobForRequest = (job, request) => {
  if (!job || !request) return job || null;
  if (!request.recurring || request.occurrenceKey === "main") return { ...job };
  const key = request.occurrenceKey;
  return {
    ...job,
    originalOccurrenceDate: key,
    occurrenceDate: key,
    startDate: key,
    endDate: key,
    __renderDate: key,
  };
};

export const TechCompletionQueue = ({ jobs = [], onOpenJob, staffName = "" }) => {
  const [requests, setRequests] = useState([]);
  const [fetchedJobs, setFetchedJobs] = useState({});
  const [open, setOpen] = useState(true);

  // Live list of open requests.
  useEffect(() => {
    const q = query(
      collection(db, TECH_COMPLETION_COLLECTION),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Ready to Invoice listener failed:", err)
    );
    return () => unsub();
  }, []);

  const jobsById = useMemo(() => {
    const map = {};
    (jobs || []).forEach((j) => {
      if (j?.id && !map[j.id]) map[j.id] = j;
    });
    return map;
  }, [jobs]);

  // Jobs that aren't in Home's list (e.g. filtered out) are read directly.
  useEffect(() => {
    const missing = requests
      .map((r) => r.jobId)
      .filter((id) => id && !jobsById[id] && !fetchedJobs[id]);
    if (!missing.length) return;
    (async () => {
      const found = {};
      await Promise.all(
        Array.from(new Set(missing)).map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "jobs", id));
            found[id] = snap.exists() ? { id, ...snap.data() } : { id, __missing: true };
          } catch {
            found[id] = { id, __missing: true };
          }
        })
      );
      setFetchedJobs((prev) => ({ ...prev, ...found }));
    })();
  }, [requests, jobsById, fetchedJobs]);

  const jobFor = (r) => jobsById[r.jobId] || fetchedJobs[r.jobId] || null;

  // Once staff completes the job/occurrence (from any page), close the request.
  useEffect(() => {
    requests.forEach((r) => {
      const job = jobFor(r);
      if (!job || job.__missing) return;
      if (isOccurrenceClosedFor(buildJobForRequest(job, r))) {
        updateDoc(doc(db, TECH_COMPLETION_COLLECTION, r.id), {
          status: "done",
          doneAt: serverTimestamp(),
        }).catch((err) => console.warn("Could not close tech completion request:", err));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, jobsById, fetchedJobs]);

  const visible = requests
    .filter((r) => {
      const job = jobFor(r);
      return !(job && !job.__missing && isOccurrenceClosedFor(buildJobForRequest(job, r)));
    })
    .sort((a, b) => {
      const ta = a.submittedAt?.seconds || 0;
      const tb = b.submittedAt?.seconds || 0;
      return ta - tb;
    });

  const sendBack = async (r) => {
    const reason = window.prompt("Why are you sending this back to the tech?", "");
    if (reason === null) return;
    try {
      await updateDoc(doc(db, TECH_COMPLETION_COLLECTION, r.id), {
        status: "returned",
        returnReason: reason.trim(),
        returnedBy: staffName || getAuth().currentUser?.email || "Office",
        returnedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      alert("Could not send this back. Please try again.");
    }
  };

  const openJob = async (r) => {
    let job = jobFor(r);
    try {
      // fresh copy so notes the tech just added are there
      const snap = await getDoc(doc(db, "jobs", r.jobId));
      if (snap.exists()) job = { id: r.jobId, ...snap.data() };
    } catch {
      /* fall back to what we have */
    }
    if (!job || job.__missing) {
      alert("This job no longer exists.");
      return;
    }
    if (typeof onOpenJob === "function") onOpenJob(buildJobForRequest(job, r), r);
  };

  return (
    <div
      style={{
        margin: "0 0 14px",
        border: `2px solid ${visible.length ? "#10b981" : "#cbd5e1"}`,
        borderRadius: 12,
        background: visible.length ? "#ecfdf5" : "#f8fafc",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 14px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          fontWeight: 800,
          fontSize: 16,
          color: "#064e3b",
        }}
      >
        <span>
          ✅ Ready to Invoice — marked completed by techs{" "}
          <span
            style={{
              marginLeft: 6,
              padding: "2px 10px",
              borderRadius: 999,
              background: visible.length ? "#10b981" : "#94a3b8",
              color: "#fff",
              fontSize: 13,
            }}
          >
            {visible.length}
          </span>
        </span>
        <span>{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 12px" }}>
          {visible.length === 0 ? (
            <div style={{ color: "#475569", fontStyle: "italic" }}>
              Nothing waiting. When a tech marks a job completed it shows up here.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {visible.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid #a7f3d0",
                  }}
                >
                  <div style={{ minWidth: 0, flex: "1 1 260px" }}>
                    <div style={{ fontWeight: 800 }}>
                      {r.wo ? `WO# ${r.wo} · ` : ""}
                      {r.siteName || "Job"}
                      {r.serviceDate ? ` · ${moment(r.serviceDate).format("MM/DD/YYYY")}` : ""}
                    </div>
                    <div style={{ fontSize: 13, color: "#475569" }}>
                      {r.serviceType ? `${r.serviceType} · ` : ""}
                      Completed by {r.submittedBy || "Tech"}
                      {r.submittedAt ? ` · ${formatWhen(r.submittedAt)}` : ""}
                    </div>
                    {r.techNote && (
                      <div style={{ fontSize: 13, marginTop: 4, whiteSpace: "pre-wrap" }}>
                        <strong>Tech note:</strong> <TranslatedText text={r.techNote} hint={r.lang} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="green-button"
                      style={{ width: "auto" }}
                      onClick={() => openJob(r)}
                    >
                      Open Job to Finish
                    </button>
                    <button
                      type="button"
                      className="grey-button"
                      style={{ width: "auto" }}
                      onClick={() => sendBack(r)}
                    >
                      Send Back
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
